import Flickity from "flickity";

export default class BaseCarouselWithMultipleSlides {
    /**
     * @var {HTMLElement}
     */
    sliderContainer = null;

    /**
     * @var {Flickity}
     */
    slider = null;

    /**
     * @var {NodeList}
     */
    slides = null;

    /**
     * @var {Number}
     */
    currentSlide = 0;

    /**
     * @var {Boolean}
     */
    preview = false;

    /**
     * @var {Number}
     */
    autoplay = 0;

    /**
     * @var {Boolean}
     */
    infinite = false;

    /**
     * @var {String}
     */
    align = "left";

    /**
     * constructor
     * @var {Boolean}
     * @var {Number}
     */
    constructor(preview, autoplay, infinite, align = "left") {
        this.preview = preview;
        this.autoplay = autoplay;
        this.infinite = infinite;
        this.align = align;
    }

    /**
     * init
     * @return void
     */
    init() {
        const container = this.$el.querySelector(".Slide__Container");
        this.sliderContainer = container.querySelector(".slider");

        if (!this.preview) {
            this.initSlider();
        }
        this.containerResize();
        window.addEventListener("resize", () => {
            this.containerResize();
        });
    }

    /**
     * initSlider
     * @return void
     */
    initSlider() {
        let options = {
            cellAlign: this.align === "wide" || this.align === "full" || !this.align ? 'left' : this.align,
            contain: true,
            wrapAround: this.infinite,
            prevNextButtons: false,
            pageDots: false,
            draggable: true,
            pauseAutoPlayOnHover: false,
            on: {
                ready: () => {
                    window.dispatchEvent(new Event("resize"));
                },
                settle: () => {
                    this.updateTabIndexes(this.slides);
                },
                change: () => {
                    this.currentSlide = this.slider.selectedIndex;
                },
            },
        };

        if (this.autoplay > 0) {
            options.autoPlay = this.autoplay * 1000;
        }

        this.slider = new Flickity(this.sliderContainer, options);

        const flickitySlider = this.sliderContainer.querySelector(".flickity-slider");
        this.slides = [...flickitySlider.children];

        this.updateTabIndexes(this.slides);

        pegasus.onResizeFinished(() => {
            this.sliderResize();
        }, true);
    }

    /**
     * nextSlide
     * @return void
     */
    nextSlide(slidesToShow) {
        if (this.preview) {
            const slider = this.$root.querySelector(".slider");
            const slides = [...slider.children];

            const slideWidth = slides[0].offsetWidth;

            // scroll to the next slide
            slider.scrollLeft += slideWidth;
        } else {
            // disable scrolling if the last slide is reached
            if((this.slider.selectedIndex >= (this.slider.cells.length - slidesToShow)) && !this.infinite) {
                return
            }
            this.slider.next();
        }
    }

    /**
     * previousSlide
     * @return void
     */
    previousSlide() {
        if (this.preview) {
            const slider = this.$root.querySelector(".slider");
            const slides = [...slider.children];

            const slideWidth = slides[0].offsetWidth;

            // scroll to the previous slide
            slider.scrollLeft -= slideWidth;
        } else {
            this.slider.previous();
        }
    }

    /**
     * sliderResize
     *
     * @return void
     */
    sliderResize() {
        if (this.sliderContainer === null) return;

        const flickitySliders =
            this.sliderContainer.querySelectorAll(".flickity-slider");

        this.sliderFullWidth = 0;

        flickitySliders.forEach((slider) => {
            let height = 0;

            this.slides.forEach((slide) => {
                slide.style.height = "";
                if (slide.offsetHeight > height) {
                    height = slide.offsetHeight;
                }
            });

            this.slides.forEach((slide) => {
                slide.style.height = height + "px";
            });

            slider.style.height = "";
            slider.style.height = height + "px";
        });
    }

    /**
     * updateTabIndexes
     *
     * Description: Updates the tabindexes of the slides this is required for accessibility.
     * Inactive slides shouldn't be accessible via the keyboard.
     *
     * @param {} slides
     * @return void
     */
    updateTabIndexes(slides) {

        slides.forEach((slide) => {
            const slideElements = slide.querySelectorAll("a, button");

            slideElements.forEach((element) => {
                if (slide.classList.contains("is-selected")) {
                    element.removeAttribute("tabindex");
                } else {
                    element.setAttribute("tabindex", "-1");
                }
            });
        });
    }

    /**
     * containerResize
     *
     * Description: In order to get the slider to break out of its parent container
     * we need to set the width of the slider to be larger than the container.
     *
     * @return void
     */
    containerResize() {
        const container = this.$root.closest(".container");

        if(!container || window.outerWidth < 768) return;

        const slider = this.$root.querySelector(".slider");

        if(!slider) return;

        // We need to get the width of the container excluding padding
        let widthWithPaddings = container.clientWidth;
        const elementComputedStyle = window.getComputedStyle(container, null);

        const containerWidth = widthWithPaddings -
        parseFloat(elementComputedStyle.paddingLeft) -
        parseFloat(elementComputedStyle.paddingRight);

        const containerVw = (containerWidth / window.innerWidth) * 100;

        // If the align is full, center or wide we need to set the slider to be the full width of the container
        let divider = this.align === "full" || this.align === "center" || this.align === "wide" ? 1 : 2;

        const sliderWidth = 100 - ((100 - containerVw) / divider);

        slider.style.width = `${sliderWidth}vw`;

        // In the page editor we use a simple scroll to display the slider instead of flickity so we need to make sure the slider is aligned properly.
        if(this.preview) {
            if(this.align === "right") {
                slider.style.marginLeft = '';
                slider.style.marginRight = `-${(100 - containerVw) / divider}vw`;
            }

            if(this.align === "left") {
                slider.style.marginRight = '';
                slider.style.marginLeft = `-${(100 - containerVw) / (divider)}vw`;
            }

        }

    }
}

document.addEventListener("alpine:init", () => {
    Alpine.data("carouselWithMultipleSlides", (preview, autoplay, infinite, align) => new BaseCarouselWithMultipleSlides(preview, autoplay, infinite, align));
});
